import { CONFIG } from '../app/config.js';
import { normalizeCharacterStyle } from './styleNormalizer.js';
import { GUEST_STYLE } from './platformTypes.js';

export class StartNgClient {
  constructor(options = {}) {
    this.connectUrl = options.connectUrl || CONFIG.PLATFORM.CONNECT_URL;
    this.connectOrigin = options.connectOrigin || CONFIG.PLATFORM.CONNECT_ORIGIN;
    this.timeoutMs = options.timeoutMs || CONFIG.PLATFORM.REQUEST_TIMEOUT_MS;

    this.iframe = null;
    this.isIframeReady = false;
    this.pendingRequests = new Map(); // requestId -> { resolve, reject, timer }
    this.requestIdCounter = 0;

    this.currentPlayer = null;
    this.isRefreshing = false;

    this.onMessage = this.onMessage.bind(this);
  }

  init() {
    if (typeof window === 'undefined') return;

    // Listen for messages from Start.ng connect page
    window.addEventListener('message', this.onMessage, false);

    // Create hidden iframe unless running in an environment without DOM
    if (!this.iframe && document.body) {
      this.iframe = document.createElement('iframe');
      this.iframe.id = 'startng-connect-frame';
      this.iframe.src = this.connectUrl;
      this.iframe.title = 'Start.ng Connect';
      this.iframe.style.position = 'absolute';
      this.iframe.style.width = '1px';
      this.iframe.style.height = '1px';
      this.iframe.style.left = '-9999px';
      this.iframe.style.top = '-9999px';
      this.iframe.style.opacity = '0';
      this.iframe.style.pointerEvents = 'none';
      this.iframe.setAttribute('aria-hidden', 'true');

      this.iframe.onload = () => {
        this.isIframeReady = true;
      };

      document.body.appendChild(this.iframe);
    }
  }

  isGuestUrl() {
    if (typeof window === 'undefined' || !window.location) return false;
    const search = window.location.search || '';
    return search.includes('guest') || search.includes('mode=guest');
  }

  onMessage(event) {
    // 1. Strict Origin and Source Validation
    if (event.origin !== this.connectOrigin) {
      return;
    }

    if (this.iframe && this.iframe.contentWindow && event.source !== this.iframe.contentWindow) {
      return;
    }

    const data = event.data;
    if (!data || typeof data !== 'object') return;

    // 2. Dispatch to pending request handler
    const { requestId } = data;
    if (requestId && this.pendingRequests.has(requestId)) {
      const { resolve, timer } = this.pendingRequests.get(requestId);
      clearTimeout(timer);
      this.pendingRequests.delete(requestId);
      resolve(data);
    }
  }

  sendRequest(type, payload = {}) {
    return new Promise((resolve, reject) => {
      const requestId = `req_${++this.requestIdCounter}_${Date.now()}`;

      // In local development or if iframe is blocked, provide a graceful fallback/mock
      const isDev = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        import.meta.env?.DEV
      );

      const timer = setTimeout(() => {
        if (this.pendingRequests.has(requestId)) {
          this.pendingRequests.delete(requestId);

          if (isDev) {
            // Local dev fallback simulation
            resolve(this.getDevMockResponse(type, payload, requestId));
          } else {
            reject(new Error(`Request ${type} timed out after ${this.timeoutMs}ms`));
          }
        }
      }, this.timeoutMs);

      this.pendingRequests.set(requestId, { resolve, reject, timer });

      const message = {
        type,
        requestId,
        ...payload,
      };

      try {
        if (this.iframe && this.iframe.contentWindow) {
          this.iframe.contentWindow.postMessage(message, this.connectOrigin);
        } else if (isDev) {
          // If no iframe, trigger dev mock quickly
          clearTimeout(timer);
          this.pendingRequests.delete(requestId);
          setTimeout(() => resolve(this.getDevMockResponse(type, payload, requestId)), 50);
        }
      } catch (err) {
        clearTimeout(timer);
        this.pendingRequests.delete(requestId);
        if (isDev) {
          resolve(this.getDevMockResponse(type, payload, requestId));
        } else {
          reject(err);
        }
      }
    });
  }

  async getCurrentPlayer() {
    // 1. Check if explicit guest URL param is active
    if (this.isGuestUrl()) {
      this.currentPlayer = {
        status: 'guest',
        player: {
          id: null,
          name: 'Guest Runner',
          handle: 'guest',
          style: normalizeCharacterStyle(GUEST_STYLE),
        },
      };
      return this.currentPlayer;
    }

    try {
      const res = await this.sendRequest('START_NG_GET_PLAYER');

      if (res && res.success && res.player) {
        this.currentPlayer = {
          status: 'signed-in',
          player: {
            id: res.player.id || null,
            name: res.player.name || 'Runner',
            handle: res.player.handle || null,
            style: normalizeCharacterStyle(res.player.style),
          },
        };
      } else {
        // Platform returned guest or unauthenticated
        this.currentPlayer = {
          status: 'guest',
          player: {
            id: null,
            name: 'Guest Runner',
            handle: 'guest',
            style: normalizeCharacterStyle(res?.player?.style || GUEST_STYLE),
          },
        };
      }
    } catch {
      // Platform error / timeout -> Recover with guest status
      this.currentPlayer = {
        status: 'guest',
        player: {
          id: null,
          name: 'Guest Runner',
          handle: 'guest',
          style: normalizeCharacterStyle(GUEST_STYLE),
        },
      };
    }

    return this.currentPlayer;
  }

  async refreshPlayerIdentity() {
    if (this.isRefreshing) return this.currentPlayer;
    this.isRefreshing = true;
    try {
      return await this.getCurrentPlayer();
    } finally {
      this.isRefreshing = false;
    }
  }

  async submitScore(score, runId) {
    if (this.currentPlayer?.status === 'guest') {
      return {
        ok: false,
        reason: 'guest_mode',
        message: 'Scores cannot be saved to the global leaderboard in Guest mode.',
      };
    }

    try {
      const res = await this.sendRequest('START_NG_SUBMIT_SCORE', { score, runId });
      if (res && res.success) {
        return {
          ok: true,
          bestScore: res.bestScore ?? score,
          rank: res.rank ?? null,
          message: 'Score saved to Start.ng!',
        };
      }
      return {
        ok: false,
        reason: res?.reason || 'submission_rejected',
        message: 'Could not save score to Start.ng.',
      };
    } catch (err) {
      return {
        ok: false,
        reason: 'timeout_or_network',
        message: err.message || 'Network error saving score.',
      };
    }
  }

  async getLeaderboard(limit = CONFIG.PLATFORM.LEADERBOARD_LIMIT) {
    try {
      const res = await this.sendRequest('START_NG_GET_LEADERBOARD', { limit });
      if (res && res.success && Array.isArray(res.leaderboard)) {
        return {
          ok: true,
          entries: res.leaderboard.map((item, idx) => ({
            rank: item.rank || idx + 1,
            name: String(item.name || 'Anonymous'),
            score: Number.isInteger(item.score) ? item.score : 0,
          })),
        };
      }
      return {
        ok: false,
        entries: [],
        reason: res?.reason || 'unavailable',
      };
    } catch {
      return {
        ok: false,
        entries: [],
        reason: 'network_error',
      };
    }
  }

  getSignInUrl() {
    const returnTo = typeof window !== 'undefined' ? encodeURIComponent(window.location.href) : '';
    return `https://start.ng/signin?return_to=${returnTo}`;
  }

  getDevMockResponse(type, payload, requestId) {
    if (type === 'START_NG_GET_PLAYER') {
      return {
        type: 'START_NG_PLAYER_RESPONSE',
        requestId,
        success: true,
        player: {
          id: 'dev_user_42',
          name: 'NeonRunner',
          handle: 'neon_sprint',
          style: {
            body: 'female',
            primaryColor: '#42e8e0',
            secondaryColor: '#f05bd5',
            skinColor: '#d6a374',
            hairColor: '#17120f',
            hair: 'ponytail',
            hat: 'cap',
            accessory: 'glasses',
          },
        },
      };
    }

    if (type === 'START_NG_SUBMIT_SCORE') {
      return {
        type: 'START_NG_SCORE_RESPONSE',
        requestId,
        success: true,
        bestScore: Math.max(payload.score, 180),
        rank: 3,
      };
    }

    if (type === 'START_NG_GET_LEADERBOARD') {
      return {
        type: 'START_NG_LEADERBOARD_RESPONSE',
        requestId,
        success: true,
        leaderboard: [
          { rank: 1, name: 'CyberDash', score: 320 },
          { rank: 2, name: 'PixelBlaze', score: 260 },
          { rank: 3, name: 'NeonRunner', score: 180 },
          { rank: 4, name: 'AstroStride', score: 140 },
          { rank: 5, name: 'GridSurfer', score: 95 },
        ],
      };
    }

    return { success: false, requestId };
  }

  dispose() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.onMessage);
    }
    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
      this.iframe = null;
    }
    for (const [, req] of this.pendingRequests) {
      clearTimeout(req.timer);
    }
    this.pendingRequests.clear();
  }
}
